/**
 * Resolves a `teamId` to a display name. `/round/get-board` returns team ids
 * only and no endpoint on the pick path carries a name, so this hook walks
 * the three-hop chain: the pool's `leagueKey`, the league catalog to find the
 * matching `leagueId`, then the team list for that league.
 */
import { usePoolGet } from "@sportsmet/pool-sdk/private";
import {
  useLeagueCatalogGet,
  useTeamList,
} from "@sportsmet/sports-sdk/private";
import { useMemo } from "react";

const CATALOG_STALE_TIME_MS = 5 * 60 * 1000;

export type typeTeamName = {
  teamId: string;
  name: string;
  abbreviation: string;
  logoUrl: string | null;
};

export type typeTeamNamesResult = {
  teamsById: Map<string, typeTeamName>;
  isLoading: boolean;
};

export function useTeamNames(input: { poolId: string }): typeTeamNamesResult {
  const pool = usePoolGet({ poolId: input.poolId });
  const leagueKey = pool.data?.data?.pool.leagueKey ?? "";

  const catalog = useLeagueCatalogGet({}, { staleTime: CATALOG_STALE_TIME_MS });

  const leagueId = useMemo(() => {
    const sports = catalog.data?.data?.sports ?? [];
    for (const sport of sports) {
      const league = sport.leagues.find(
        (candidate) => candidate.leagueKey === leagueKey,
      );
      if (league) return league.leagueId;
    }
    return "";
  }, [catalog.data, leagueKey]);

  const teamList = useTeamList(
    { leagueId },
    { enabled: leagueId !== "", staleTime: CATALOG_STALE_TIME_MS },
  );

  const teamsById = useMemo(() => {
    const teams = teamList.data?.data?.teams ?? [];
    const map = new Map<string, typeTeamName>();
    for (const team of teams) {
      map.set(team.teamId, {
        teamId: team.teamId,
        name: team.name,
        abbreviation: team.abbreviation,
        logoUrl: team.logoUrl ?? null,
      });
    }
    return map;
  }, [teamList.data]);

  const isLoading =
    pool.isLoading ||
    catalog.isLoading ||
    (leagueId !== "" && teamList.isLoading && teamsById.size === 0);

  return { teamsById, isLoading };
}
